// Pure string/digit manipulation only - no Number()/parseFloat (banned outside @taler/domain,
// AGENTS.md's money rule) and no domain import (packages/ui may not depend on @taler/domain).
// The canonical value this module produces and consumes always uses '.' as its decimal point,
// regardless of locale; only the *display* string uses the caller's separators.

export interface AmountFormat {
  readonly groupSeparator: string
  readonly decimalSeparator: string
  /** 0 for a currency with no minor unit (HUF); the fraction is capped here, never rounded. */
  readonly decimalDigits: number
  readonly allowNegative?: boolean
}

const isDigit = (ch: string): boolean => ch >= '0' && ch <= '9'

/**
 * Reduces one keystroke's raw text to the canonical form: an optional leading '-', digits,
 * and at most one '.', its fraction capped at `format.decimalDigits`. Everything that cannot
 * be typed into a valid amount - letters, a second separator, excess fraction digits, a
 * currency symbol - is dropped rather than rejected wholesale. The field's own group
 * separator is always noise here regardless of position, which is what keeps live typing
 * self-consistent: the comma this function itself inserted into the display a moment ago must
 * never be mistaken for a decimal point on the very next keystroke. `cleanPastedAmountInput`
 * below is the one that has to reason about a foreign locale's own formatting instead.
 */
function cleanTypedAmountInput(raw: string, format: AmountFormat): string {
  let negative = false
  let sawMinus = false
  let sawSeparator = false
  let fractionDigits = 0
  let integer = ''
  let fraction = ''
  for (const ch of raw) {
    if (ch === format.groupSeparator || ch === ' ' || ch === '\u00A0') continue
    if (ch === '-') {
      if (format.allowNegative === true && !sawMinus && integer === '' && !sawSeparator) negative = true
      sawMinus = true
      continue
    }
    if (isDigit(ch)) {
      if (sawSeparator) {
        if (fractionDigits < format.decimalDigits) {
          fraction += ch
          fractionDigits++
        }
      } else {
        integer += ch
      }
      continue
    }
    // Recognised even at zero decimal digits, so a pasted "1300.0000" truncates to "1300"
    // instead of its fraction digits sliding into the integer part.
    if (!sawSeparator && (ch === '.' || ch === ',')) sawSeparator = true
  }
  const body = sawSeparator && format.decimalDigits > 0 ? `${integer}.${fraction}` : integer
  return negative && body !== '' ? `-${body}` : body
}

/**
 * A pasted string can carry a *different* locale's own grouping and decimal convention, and
 * that convention can use the exact character this field's own locale uses for the opposite
 * role - a Hungarian-grouped "1 234,56" pasted into an English field has a comma that is HU's
 * decimal point sitting where EN's own thousands separator would be. Stripping this field's
 * `groupSeparator` unconditionally (as the typed path does, correctly, for self-consistency)
 * would erase that comma before it ever gets a chance to be read as a decimal point, turning
 * 1234.56 into 123456 - a silent hundredfold error with no signal to the person who pasted it.
 *
 * So a paste pools every '.' and ',' occurrence uniformly, with no locale carve-out: the LAST
 * one is the decimal point, and every earlier one, whichever character it is, is grouping
 * noise. The only place this needs a tiebreak is the shape that is genuinely ambiguous on its
 * own - exactly one separator with exactly three digits after it and nothing else ("1,234" is
 * a thousand in English, one-point-two-three-four in Hungarian) - which this field's own
 * locale settles: the character reads as *this field's* group separator only when it actually
 * is one; otherwise (it is this field's own decimal separator, or a character foreign to both
 * of this field's roles, e.g. a dot pasted into a Hungarian field) it falls back to the
 * general last-wins reading, which is also what makes a wire-scale "1300.0000" pasted into a
 * zero-decimal HUF field truncate correctly - its dot matches neither HU role and has four
 * trailing digits rather than three, so the tiebreak never fires and the general rule alone
 * drops the fraction.
 */
function cleanPastedAmountInput(raw: string, format: AmountFormat): string {
  let negative = false
  let sawMinus = false
  const digits: string[] = []
  const sepDigitCounts: number[] = []
  const sepChars: string[] = []
  for (const ch of raw) {
    if (ch === ' ' || ch === '\u00A0') continue
    if (ch === '-') {
      if (format.allowNegative === true && !sawMinus && digits.length === 0 && sepChars.length === 0) {
        negative = true
      }
      sawMinus = true
      continue
    }
    if (isDigit(ch)) {
      digits.push(ch)
      continue
    }
    if (ch === '.' || ch === ',') {
      sepDigitCounts.push(digits.length)
      sepChars.push(ch)
    }
  }
  const finish = (body: string): string => (negative && body !== '' ? `-${body}` : body)
  if (sepChars.length === 0) return finish(digits.join(''))

  let decimalAt = sepDigitCounts.length - 1
  if (sepChars.length === 1) {
    const trailingDigits = digits.length - sepDigitCounts[0]!
    if (trailingDigits === 3 && sepChars[0] === format.groupSeparator) decimalAt = -1
  }
  if (decimalAt === -1) return finish(digits.join(''))

  const splitAt = sepDigitCounts[decimalAt]!
  const integer = digits.slice(0, splitAt).join('')
  const fraction =
    format.decimalDigits > 0 ? digits.slice(splitAt, splitAt + format.decimalDigits).join('') : ''
  return finish(format.decimalDigits > 0 ? `${integer}.${fraction}` : integer)
}

/** `pasted: true` for a paste/drop event (`InputEvent.inputType`); omitted or `false` for an
 * ordinary keystroke. See {@link cleanPastedAmountInput} for why the two need different rules. */
export function cleanAmountInput(raw: string, format: AmountFormat, pasted = false): string {
  return pasted ? cleanPastedAmountInput(raw, format) : cleanTypedAmountInput(raw, format)
}

/** A canonical value with a trailing '.' (fraction started but empty) is mid-edit, not a number yet. */
export function stripPendingSeparator(clean: string): string {
  return clean.endsWith('.') ? clean.slice(0, -1) : clean
}

function groupDigits(digits: string, separator: string): string {
  if (digits === '') return ''
  let out = ''
  for (let i = digits.length; i > 0; i -= 3) {
    const chunk = digits.slice(Math.max(0, i - 3), i)
    out = out === '' ? chunk : `${chunk}${separator}${out}`
  }
  return out
}

/** Renders a canonical value with the locale's group and decimal separators. */
export function formatAmountDisplay(clean: string, format: AmountFormat): string {
  const negative = clean.startsWith('-')
  const unsigned = negative ? clean.slice(1) : clean
  const dot = unsigned.indexOf('.')
  const integerPart = dot === -1 ? unsigned : unsigned.slice(0, dot)
  const fractionPart = dot === -1 ? undefined : unsigned.slice(dot + 1)
  const grouped = groupDigits(integerPart, format.groupSeparator)
  const fractionDisplay = fractionPart === undefined ? '' : `${format.decimalSeparator}${fractionPart}`
  return `${negative ? '-' : ''}${grouped}${fractionDisplay}`
}

function countDigits(value: string): number {
  let n = 0
  for (const ch of value) if (isDigit(ch)) n++
  return n
}

/**
 * Where the caret belongs in the newly-formatted string, carried over by digit count rather
 * than by character offset - a group separator inserted or removed by this keystroke must
 * never shift the caret relative to the digits the user is actually looking at.
 */
export function nextCaretIndex(rawBeforeFormat: string, caretInRaw: number, formatted: string): number {
  const before = rawBeforeFormat.slice(0, caretInRaw)
  const digitsBeforeCaret = countDigits(before)
  if (digitsBeforeCaret === 0) return before.includes('-') ? Math.min(1, formatted.length) : 0
  // A decimal point typed after the last digit is part of what the caret follows: the caret
  // steps over it, so the next digit lands in the fraction rather than the integer part.
  const afterPendingSeparator = /[.,]$/.test(before)
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (isDigit(formatted[i]!)) {
      seen++
      if (seen === digitsBeforeCaret) {
        const next = i + 1
        return afterPendingSeparator && next < formatted.length && !isDigit(formatted[next]!)
          ? next + 1
          : next
      }
    }
  }
  return formatted.length
}
